import { UI_PRIMITIVE__button } from '@/components/ui/UI_PRIMITIVE__button';
import { use_dispatch } from '@/hooks/_base/use_dispatch';
import { use_effect } from '@/hooks/_base/use_effect';
import { use_slice_workspace } from '@/hooks/store/use_slices';
import { useToast } from '@/hooks/use-toast';
import i18n_base_ui from '@/locales/keys/i18n_base_ui.json';
import { ENUM__i18n_namespace } from '@/services/i18n/ENUM__i18n_namespace';
import { remote_workspace_user_invitation_reply } from '@/state/workspace/user_invitation/remote_workspace_user_invitation_reply';
import { set_current_workspace } from '@/utils/utils_client_workspace_child_entity_viewers';
import { RequestWorkspaceUserInvitationReply } from '@shared/dto/+workspace/workspace_user_invitation/_requests/request_workspace_user_invitation_reply';
import { DTOWorkspaceUserInvitation } from '@shared/dto/+workspace/workspace_user_invitation/dto_workspace_user_invitation';
import { DTO__database_entity } from '@shared/dto/_database_entity/DTO__database_entity';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export function WatcherInvitationToWorkspace() {
  const { t } = useTranslation(ENUM__i18n_namespace.BASE_UI);

  const dispatch = use_dispatch();
  const { toast, dismiss } = useToast();

  const { invitations_map_to_other_workspaces, is_all_data_loaded, workspace_current } = use_slice_workspace([
    'invitations_map_to_other_workspaces',
    'is_all_data_loaded',
    'workspace_current',
  ]);

  //key - invitation_id, value - toast_id
  const [toast_map, set_toast_map] = useState<Map<string, string>>(new Map());

  /*
   * EFFECTS
   */
  use_effect(() => {
    if (!is_all_data_loaded) return;

    hide_all_toasts();

    let invitations = Array.from(invitations_map_to_other_workspaces.values()).sort(DTO__database_entity.sort_by_created_at_newest_first);

    invitations.forEach(show_toast);

    return () => hide_all_toasts();
  }, [invitations_map_to_other_workspaces, is_all_data_loaded]);

  /*
   * EVENTS
   */
  function onButtonGoToWorkspaceClicked(toast_id: string, invitation: DTOWorkspaceUserInvitation) {
    dismiss(toast_id);

    set_current_workspace(invitation.workspace_id);
  }

  async function onButtonAcceptClicked(toast_id: string, invitation: DTOWorkspaceUserInvitation) {
    dismiss(toast_id);

    let request = new RequestWorkspaceUserInvitationReply();
    request.workspace_id = invitation.workspace_id;
    request.invitation_id = invitation._id;
    request.accepted = true;

    await dispatch(remote_workspace_user_invitation_reply(request));

    try_show_toast_go_to_workspace(invitation);
  }

  async function onButtonRejectClicked(toast_id: string, invitation: DTOWorkspaceUserInvitation) {
    dismiss(toast_id);

    let request = new RequestWorkspaceUserInvitationReply();
    request.workspace_id = invitation.workspace_id;
    request.invitation_id = invitation._id;
    request.accepted = false;

    await dispatch(remote_workspace_user_invitation_reply(request));
  }

  /*
   * METHODS
   */

  function try_show_toast_go_to_workspace(invitation: DTOWorkspaceUserInvitation) {
    if (workspace_current._id == invitation.workspace_id) return;

    let toast_data = toast({
      title: t(i18n_base_ui.toasts.invitation_to_workspace_accepted.title),
      action: (
        <UI_PRIMITIVE__button variant="outline-on-bar" onClick={() => onButtonGoToWorkspaceClicked(toast_data.id, invitation)}>
          {t(i18n_base_ui.toasts.invitation_to_workspace_accepted.action.button.go_to_workspace, { workspace_name: invitation.workspace_name })}
        </UI_PRIMITIVE__button>
      ),
    });
  }

  function hide_all_toasts() {
    if (toast_map.size == 0) return;

    //hide previous invitations
    for (let [_, value] of toast_map) {
      dismiss(value);
    }

    set_toast_map(new Map());
  }

  function show_toast(invitation: DTOWorkspaceUserInvitation) {
    let toast_data = toast({
      title: t(i18n_base_ui.toasts.invitation_to_workspace_received.title),
      description: t(i18n_base_ui.toasts.invitation_to_workspace_received.decription, {
        owner_full_name: invitation.owner_full_name,
        workspace_name: invitation.workspace_name,
      }),
      duration: Infinity,
      hide_button_close: true,
      action: (
        <>
          <UI_PRIMITIVE__button variant="outline-on-bar" onClick={() => onButtonAcceptClicked(toast_data.id, invitation)}>
            {t(i18n_base_ui.toasts.invitation_to_workspace_received.action.button.accept)}
          </UI_PRIMITIVE__button>
          <UI_PRIMITIVE__button variant="outline-on-bar" onClick={() => onButtonRejectClicked(toast_data.id, invitation)}>
            {t(i18n_base_ui.toasts.invitation_to_workspace_received.action.button.reject)}
          </UI_PRIMITIVE__button>
        </>
      ),
    });

    let toastMapNew = new Map(toast_map);
    toastMapNew.set(invitation._id, toast_data.id);

    set_toast_map(toastMapNew);
  }

  return <></>;
}
